import React from 'react';
import { useParams } from 'react-router-dom';

import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { Avatar } from '@oysterjs/ui/avatar';
import { Button } from '@oysterjs/ui/button';
import { Textarea } from '@oysterjs/ui/textarea';

import {
  ClientNoteFieldsFragment,
  useCreateClientNoteMutation,
  useDeleteClientNoteMutation,
  useGetClientNotesQuery,
  useUpdateClientNoteMutation
} from '~/api/graphql';
import { failed } from '@oysterjs/core/graphql/errors';

import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import { FormError } from '~/components/form-error';
import { ClientTabEmptyState, ClientTabRoute } from '~/components/clients/client-panels';

export const initialsOf = (name?: string | null, email?: string | null): string => {
  const source = (name || email || '').trim();
  if (!source) return '?';
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const timeFormatter = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const weekdayFormatter = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

const startOfToday = (): number => {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  return midnight.getTime();
};

/**
 * "Today 9:50 AM" / "Mon 8:51 AM" / "Aug 12, 2026". Recent notes are placed by
 * time of day because that is how someone remembers writing them, older ones by
 * date. The locale is pinned so the rendering does not vary by machine.
 */
export const formatNoteTime = (value: Date): string => {
  const when = new Date(value);
  const today = startOfToday();
  const dayMs = 24 * 60 * 60 * 1000;
  if (when.getTime() >= today) return `Today ${timeFormatter.format(when)}`;
  if (when.getTime() >= today - 6 * dayMs) {
    return `${weekdayFormatter.format(when)} ${timeFormatter.format(when)}`;
  }
  return dateFormatter.format(when);
};

const SAVE_FAILED = 'Could not save. Your text is still here -- try again.';

const NoteComposer = ({ clientId, onSaved }: { clientId: string; onSaved: () => void }) => {
  const [body, setBody] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [createNote, { loading }] = useCreateClientNoteMutation();

  const submit = async () => {
    if (!body.trim()) return;
    setError(null);
    // The draft is only cleared once the server has it.
    const result = await createNote({ variables: { clientId, body } });
    if (failed(result)) {
      setError(SAVE_FAILED);
      return;
    }
    setBody('');
    onSaved();
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
      <Textarea
        rows={3}
        value={body}
        placeholder="Add a note about this client..."
        onChange={(e) => setBody(e.currentTarget.value)}
        aria-label="Note body"
      />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          Visible to everyone at your agency
        </span>
        <Button onClick={submit} disabled={loading || !body.trim()} color="sky">
          Add note
        </Button>
      </div>
      <FormError className="mt-2 text-xs text-red-600 dark:text-red-400" message={error} />
    </div>
  );
};

const NoteRow = ({
  note,
  deletable,
  onChanged
}: {
  note: ClientNoteFieldsFragment;
  /** Only the author may delete a note; anyone at the agency may correct one. */
  deletable: boolean;
  onChanged: () => void;
}) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(note.body);
  const [error, setError] = React.useState<string | null>(null);

  const [updateNote, { loading: saving }] = useUpdateClientNoteMutation();
  const [deleteNote] = useDeleteClientNoteMutation();

  const save = async () => {
    if (!draft.trim()) return;
    setError(null);
    // The editor stays open on failure, so the edit is not lost with it.
    const result = await updateNote({ variables: { id: note.id, body: draft } });
    if (failed(result)) {
      setError(SAVE_FAILED);
      return;
    }
    setEditing(false);
    onChanged();
  };

  const remove = async () => {
    setError(null);
    const result = await deleteNote({ variables: { id: note.id } });
    if (failed(result)) {
      setError('Could not delete this note. Try again.');
      return;
    }
    onChanged();
  };

  return (
    <div className="flex gap-3 py-4">
      <Avatar
        initials={initialsOf(note.author?.name, note.author?.email)}
        className="size-8 shrink-0 bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {note.author?.name || note.author?.email || 'Unknown author'}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {formatNoteTime(note.createdAt)}
          </span>
        </div>

        {editing ? (
          <div className="mt-2 space-y-2">
            <Textarea
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              aria-label="Edit note"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={save} disabled={saving || !draft.trim()}>
                Save
              </Button>
              <Button plain onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-300">
            {note.body}
          </p>
        )}

        <FormError className="mt-2 text-xs text-red-600 dark:text-red-400" message={error} />

        {!editing && (
          <div className="mt-2 flex gap-3 text-xs">
            <button
              type="button"
              className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 dark:text-neutral-400"
              onClick={() => {
                setDraft(note.body);
                setEditing(true);
              }}
            >
              Edit
            </button>
            {deletable && (
              <button
                type="button"
                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                onClick={remove}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ClientNotes = ({ clientId }: { clientId: string }) => {
  // cache-first answers from the page the route loader just fetched.
  const { data, refetch } = useGetClientNotesQuery({
    variables: { clientId },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });
  const currentUserId = useOptionalCurrentAgencyUser()?.id;

  const notes = data?.clientNotes ?? [];

  return (
    <div className="space-y-4">
      <NoteComposer clientId={clientId} onSaved={() => refetch()} />

      {notes.length === 0 ? (
        <ClientTabEmptyState
          icon={ChatBubbleLeftEllipsisIcon}
          title="No notes yet"
          description="Notes are how your team keep context on this client. Add the first one above."
        />
      ) : (
        <div className="divide-y divide-neutral-100 rounded-lg bg-white px-4 shadow dark:divide-neutral-700 dark:bg-neutral-800">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              deletable={!!currentUserId && note.author?.id === currentUserId}
              onChanged={() => refetch()}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const ClientNotesTab = () => {
  const { id = '' } = useParams();
  return <ClientTabRoute noun="notes">{() => <ClientNotes clientId={id} />}</ClientTabRoute>;
};
